import { Ammos } from "../definitions/items/ammos";
import { HealingItems } from "../definitions/items/healingItems";

/*
    How full the bag is (owner request of 16 October 2026, from a tester: "something on the hud which also shows
    backpack usage %"). The inventory screen draws the backpack as a grid of squares, more of them the better the
    backpack (client/src/scripts/managers/inventoryScreen.ts). A square holds one kind of item, as many as the backpack
    has room for (so many gauze, so many 9 mm rounds...): its stack. The percent is how much of that grid is filled: a
    kind fills its squares as far as its stacks are filled (count / room: a full stack fills one square), and the grid's
    squares together are 100%. So picking anything up raises it, dropping lowers it, an empty bag is 0%, and a bigger
    backpack (more squares, bigger stacks) shows less for the same things.

    It used to be the average of the kinds carried (the owner, the same day: "how the fuck does that percentage work...
    shit starts at 100% or something"): five gauze in the starting bag were 100%, and a cola picked up after them took
    it down to 75%.

    Ammo and wood go on into the next empty square once their stack is full (the owner, 17 October 2026: "inventory
    limits for wood are stupid, also ammo, why dont they just fill up another square if its empty after reaching the
    square limit"): `bagRoomFor`. Healing items too since 0.97 (a tester's, passed on by the owner on 23 October 2026:
    "fix these tester complaints please": "without a bag upgrade, bandage space for instance is capped out at 5, even when
    the bag isn't full ... could be worth thinking about to just remove per item restrains on max capacity and just
    operate with the bag space overall"): five field dressings to a square in the starting bag, then the next empty
    square. Grenades keep to their one square (weapons: how many a backpack carries is part of the fight). Every kind can
    still always fill its first square, however many other kinds are carried, as before.
*/

/** the backpack's grid on the inventory screen: six squares a row */
export const BAG_GRID_COLUMNS = 6;
/** its rows by backpack level: one for the starting bag, more the better the backpack */
const BAG_GRID_ROWS: readonly number[] = [1, 2, 2, 3, 4];

/** how many rows of squares a backpack of this level has */
export function bagGridRows(level: number): number {
    return BAG_GRID_ROWS[Math.min(BAG_GRID_ROWS.length - 1, Math.max(0, Math.floor(level)))];
}

/** how many squares a backpack of this level has: what 100% is */
export function bagCells(level: number): number {
    return bagGridRows(level) * BAG_GRID_COLUMNS;
}

/** what the rules need of a backpack: its stack (room) for each kind, and its level (how many squares it has) */
export interface BagBackpack {
    readonly maxCapacity: Readonly<Record<string, number>>
    readonly level: number
}

/** how many squares `count` of a kind fill, `room` to a square (none for a kind without a limit, or carried without end) */
export function bagSquares(count: number, room: number): number {
    if (!Number.isFinite(room) || room <= 0 || !Number.isFinite(count) || count <= 0) return 0;
    return Math.ceil(count / room);
}

/**
 * whether a kind goes on into an empty square once its square is full: ammo and wood (not ammo handed out without end),
 * and healing items (0.97); not grenades
 */
export function spillsOver(idString: string): boolean {
    const ammo = Ammos.fromStringSafe(idString);
    if (ammo !== undefined) return !ammo.ephemeral;
    return HealingItems.fromStringSafe(idString) !== undefined;
}

/** how many squares everything carried fills, but the kind `except` */
function squaresBut(items: Readonly<Record<string, number>>, backpack: BagBackpack, except: string): number {
    let squares = 0;
    for (const [idString, room] of Object.entries(backpack.maxCapacity)) {
        if (idString !== except) squares += bagSquares(items[idString] ?? 0, room);
    }
    return squares;
}

/**
 * The most of a kind the bag holds, with what else is carried: its one square (always, however full the bag), and for
 * ammo, wood and healing items every empty square besides, a full stack to each. Infinity for a kind without a limit (power cells);
 * nothing for a kind the bag doesn't carry (a scope)
 */
export function bagRoomFor(idString: string, items: Readonly<Record<string, number>>, backpack: BagBackpack): number {
    const room = backpack.maxCapacity[idString] ?? 0;
    if (!Number.isFinite(room)) return room > 0 ? room : 0;
    if (room <= 0) return 0;
    if (!spillsOver(idString)) return room;
    return room * Math.max(1, bagCells(backpack.level) - squaresBut(items, backpack, idString));
}

/**
 * What of each kind carried a backpack keeps (put on in place of a bigger one; the rest is dropped): every kind its
 * first square, as it always could, then more squares of ammo, wood and healing items while empty ones are left, the kinds in
 * `order` first (the ammo of the guns held), then the others
 */
export function fitInBag(items: Readonly<Record<string, number>>, backpack: BagBackpack, order: readonly string[] = []): Record<string, number> {
    const keep: Record<string, number> = {};
    let free = bagCells(backpack.level);
    for (const [idString, room] of Object.entries(backpack.maxCapacity)) {
        const count = items[idString] ?? 0;
        if (!Number.isFinite(count) || count <= 0) continue;
        keep[idString] = Number.isFinite(room) ? Math.min(count, Math.max(0, room)) : count;
        if (Number.isFinite(room) && keep[idString] > 0) free--;
    }
    const kinds = [...order, ...Object.keys(backpack.maxCapacity).filter((idString) => !order.includes(idString))];
    for (const idString of kinds) {
        const room = backpack.maxCapacity[idString] ?? 0;
        const count = items[idString] ?? 0;
        const kept = keep[idString] ?? 0;
        if (free <= 0) break;
        if (!spillsOver(idString) || !Number.isFinite(room) || room <= 0 || !Number.isFinite(count) || count <= kept) continue;
        const squares = Math.min(free, Math.ceil((count - kept) / room));
        keep[idString] = kept + Math.min(count - kept, squares * room);
        free -= squares;
    }
    return keep;
}

export interface BagRoom {
    readonly idString: string
    /**
     * how many are carried (of a grenade never more than its square: the dead ignore the limits; of ammo, wood or a
     * healing item, all of it, even when the first squares of other kinds taken since leave it more than it could pick
     * up now)
     */
    readonly count: number
    /** how many a square holds (a full stack) */
    readonly room: number
    /** how many squares they fill (ammo, wood and healing items can fill more than one) */
    readonly squares: number
    /** the most the bag holds of them now, with what else is carried (`bagRoomFor`) */
    readonly most: number
}

export interface BagFill {
    /** from 0 (empty) to 1 (the whole grid filled) */
    readonly fill: number
    /** the same, as the whole percent shown: 100 only when the grid is filled, 1 at least with anything in the bag */
    readonly percent: number
    /** the grid's squares (100%) */
    readonly cells: number
    /** how many squares' worth the things carried fill (a full stack is one) */
    readonly filled: number
    /** each kind counted (the ammo of the guns held too, with none of it) */
    readonly rooms: readonly BagRoom[]
    /** the kinds the bag can't take another of */
    readonly full: readonly string[]
}

/**
 * @param items how many of each item the player carries
 * @param backpack the backpack worn: its stack for each kind, and its level (how many squares its grid has)
 * @param alsoCount kinds listed even when none are carried (the ammo of the guns held: they fill nothing)
 */
export function bagFill(
    items: Readonly<Record<string, number>>,
    backpack: BagBackpack,
    alsoCount: readonly string[] = []
): BagFill {
    const rooms: BagRoom[] = [];
    for (const [idString, room] of Object.entries(backpack.maxCapacity)) {
        // a kind without a limit (power cells) takes no room
        if (!Number.isFinite(room) || room <= 0) continue;
        const carried = items[idString] ?? 0;
        // ammo the game hands out without end isn't carried in the bag
        if (!Number.isFinite(carried)) continue;
        if (carried <= 0 && !alsoCount.includes(idString)) continue;
        const most = bagRoomFor(idString, items, backpack);
        const count = spillsOver(idString) ? Math.max(0, carried) : Math.min(Math.max(0, carried), room);
        rooms.push({ idString, count, room, squares: bagSquares(count, room), most });
    }

    const cells = bagCells(backpack.level);
    // (more than the grid when kinds took their first squares in a bag already full: 100%)
    const filled = rooms.reduce((sum, { count, room }) => sum + count / room, 0);
    const fill = Math.min(1, filled / cells);
    const full = rooms.filter(({ count, most }) => count >= most).map(({ idString }) => idString);
    const percent = filled >= cells ? 100 : filled <= 0 ? 0 : Math.min(99, Math.max(1, Math.round(fill * 100)));
    return { fill, percent, cells, filled, rooms, full };
}
